import React, { useState, useEffect } from "react";
import { Edit, Trash2, X } from "lucide-react";

import useDocumentTitle from '../../hooks/useDocumentTitle';
import DateFormatter from '../../hooks/DateFormatter';
import ShimmerLoader from '../../hooks/ShimmerLoader';
import Pagination from "../../hooks/Pagination";
import { useAuth } from "../../context/AuthContext";
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { getproducts, updateProductPost, deleteProduct } from "../../services/api";
import { Link } from "react-router-dom";

export default function Products() {
    const { user } = useAuth();
    useDocumentTitle('Welcome to Admin | Products');
    const [productslist, setProducts] = useState([]);
    const [producttypelist, setProductTypeList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('add');
    const [formData, setFormData] = useState({
        product_name: '',
        product_type: '',
        total_qty: '',
        // sold_qty: '',
        buying_price: '',
        selling_price: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const perPage = 100;

    const fetchProducts = async (perPage, currentPage) => {
        try {
            setLoading(true);
            const response = await getproducts(perPage, currentPage);
            setProducts(response.data.data || []);
            setProductTypeList(response.producttypelist || []);
            setCurrentPage(response.current_page);
            setTotalPages(response.last_page);
        } catch (err) {
            console.error('Fetch products error:', err); // Debug log
            setError(err.message || 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts(perPage, currentPage);
    }, [currentPage]);

    // Validation rules
    const validateForm = () => {
        const errors = {};

        if (!formData.product_name.trim()) {
            errors.product_name = 'Product name is required';
        }

        if (!formData.product_type) {
            errors.product_type = 'Product type is required';
        }

        if (!formData.total_qty || formData.total_qty < 0) {
            errors.total_qty = 'Valid quantity is required';
        }
        // if (!formData.sold_qty || formData.sold_qty < 0) {
        //     errors.sold_qty = 'Valid quantity is required';
        // }

        if (!formData.buying_price || formData.buying_price < 0) {
            errors.buying_price = 'Valid buying price is required';
        }

        if (!formData.selling_price || formData.selling_price < 0) {
            errors.selling_price = 'Valid selling price is required';
        }

        if (parseFloat(formData.selling_price) < parseFloat(formData.buying_price)) {
            errors.selling_price = 'Selling price should be greater than buying price';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };


    // Then update your handleSubmit function:
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccess('');
        setFormErrors({}); // Clear previous errors

        try {
            // Prepare payload
            const payload = {
                product_name: formData.product_name,
                product_type: parseInt(formData.product_type),
                total_qty: parseInt(formData.total_qty),
                // sold_qty: parseInt(formData.sold_qty),
                buying_price: parseFloat(formData.buying_price),
                selling_price: parseFloat(formData.selling_price),

            };

            // Add product_id only for edit mode
            if (modalType === 'edit' && selectedProduct) {
                payload.product_id = selectedProduct.id;
            }

            const data = await updateProductPost(payload);

            // Handle API response
            if (data.status && data.status.success === true) {
                toastr.success(modalType === 'add' ? 'Product added successfully!' : 'Product updated successfully!');
                setShowModal(false);

                setTimeout(() => {
                    window.location.href = `/products/details/${data?.productID}`
                }, 1500);
                // await fetchProducts(perPage, currentPage);

                setTimeout(() => {
                    setShowModal(false);
                    resetForm();
                    setSuccess('');
                }, 1500);
            } else {
                // API returned error in response
                // handleApiError(data);
                toastr.error(data.message)
            }

        } catch (err) {
            // handleApiError(err);
            // toastr.error(data.message)
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            product_name: '',
            product_type: '',
            total_qty: '',
            // sold_qty: '',
            buying_price: '',
            selling_price: ''
        });
        setFormErrors({});
        setSelectedProduct(null);
    };

    // Open modal for adding new product
    const handleAddNew = () => {
        setModalType('add');
        setShowModal(true);
        resetForm();
    };

    // Open modal for editing product
    const handleEdit = (product) => {

        setModalType('edit');
        setSelectedProduct(product);
        setFormData({
            product_id: product.id || '',
            product_name: product.product_name || '',
            product_type: product.product_type || '',
            total_qty: product.total_qty?.toString() || '',
            // sold_qty: product.sold_qty?.toString() || '',
            buying_price: product.buying_price?.toString() || '',
            selling_price: product.selling_price?.toString() || '',
        });
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
        setError('');
        setSuccess('');
    };

    const [showDropdown, setShowDropdown] = useState(null);

    // Add this function for details navigation
    const handleDetails = (product) => {
        // Navigate to product details page
        window.location.href = `/products/${product.id}`;
        // Or if using React Router:
        // navigate(`/products/${product.product_id}`);
    };

    // Add click outside handler to close dropdowns
    useEffect(() => {
        const handleClickOutside = () => {
            setShowDropdown(null);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);
    const handleDelete = async (productId, productName) => {
        if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingId(productId);
            const data = await deleteProduct(productId);

            if (data.status && data.status.success === true) {
                toastr.success('Product deleted successfully!');
                // Refresh the products list
                await fetchProducts(perPage, currentPage);
            } else {
                toastr.error(data.message || 'Failed to delete product', 'error');
            }
        } catch (err) {
            toastr.error(err.message || 'Failed to delete product', 'error');
        } finally {
            setDeletingId(null);
            setShowDropdown(null);
        }
    };
    return (
        <>
            <div className="flex flex-col md:flex-row gap-4 md:gap-4">
                {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
                {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{success}</div>}

                <div className="w-full bg-white shadow-lg rounded-2xl p-6">
                    <div className="grid grid-flow-col grid-rows-3 gap-4">
                        <div className="row-start-1 row-end-4">
                            <h2 className="text-2xl font-bold mb-4">Products List</h2>
                        </div>
                        <div className="row-start-1 row-end-4 text-right">
                            <button
                                type="button"
                                onClick={handleAddNew}
                                className="py-2 px-4 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors"
                            >
                                Add New
                            </button>
                        </div>
                    </div>
                    <div className="overflow-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-gray-700 bg-gray-100">
                                <tr>
                                    <th className="p-3">Product Name</th>
                                    <th className="p-3">Product Type</th>
                                    <th className="p-3">Total Quantity</th>
                                    <th className="p-3">Sold</th>
                                    <th className="p-3">Buying GT coins</th>
                                    <th className="p-3">Selling GT coins</th>
                                    <th className="p-3">Created On</th>
                                    <th className="p-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {loading ? (
                                    <tr>
                                        <td className="p-3" colSpan={8}>
                                            <ShimmerLoader />
                                            <ShimmerLoader />
                                            <ShimmerLoader />
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {!loading && productslist.length > 0 ? (
                                            productslist.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="p-3">{item.product_name}</td>
                                                    <td className="p-3">
                                                        {producttypelist.find((p) => p.id === item.product_type)?.name || 'N/A'}
                                                    </td>
                                                    <td className="p-3">{item.total_qty}</td>
                                                    <td className="p-3">{item.sold_qty}</td>
                                                    <td className="p-3">{item.buying_price}</td>
                                                    <td className="p-3">{item.selling_price}</td>
                                                    <td className="p-3">{DateFormatter(item?.created_at, 'dd-MMM-yy h:mm a')}</td>
                                                    <td className="p-3 text-center">
                                                        <div className="relative inline-block text-left">
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-[#f4a406] hover:bg-gray-100 rounded-full transition-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowDropdown(showDropdown === item.id ? null : item.id);
                                                                }}
                                                                disabled={deletingId === item.id}
                                                            >
                                                                {deletingId === item.id ? (
                                                                    <div className="w-4 h-4 border-2 border-[#f4a406] border-t-transparent rounded-full animate-spin"></div>
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                                                                        <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
                                                                    </svg>
                                                                )}
                                                            </button>

                                                            {showDropdown === item.id && (
                                                                <div className="absolute right-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                                    <div className="py-1">
                                                                        {/* <button
                                                                            onClick={() => {
                                                                                handleEdit(item);
                                                                                setShowDropdown(null);
                                                                            }}
                                                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                                        >
                                                                            <Edit size={14} />
                                                                            Edit
                                                                        </button> */}
                                                                        <Link
                                                                            to={`/products/details/${item.id}`}
                                                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                            Details
                                                                        </Link>
                                                                        <button
                                                                            onClick={() => handleDelete(item.id, item.product_name)}
                                                                            disabled={deletingId === item.id}
                                                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                            {deletingId === item.id ? 'Deleting...' : 'Delete'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="p-3 text-center" colSpan={8}>
                                                    No products found
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                        <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-2xl"> {/* Increased from max-w-md to max-w-2xl */}
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold">
                                {modalType === 'add' ? 'Add New Product' : 'Edit Product'}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                                disabled={submitting}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4"> {/* Two column grid */}
                                {/* Product Name */}
                                <div className="md:col-span-2"> {/* Full width */}
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Product Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="product_name"
                                        value={formData.product_name}
                                        onChange={handleInputChange}
                                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors.product_name ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter product name"
                                        disabled={submitting}
                                    />
                                    {formErrors.product_name && (
                                        <p className="text-red-500 text-xs mt-1">{formErrors.product_name}</p>
                                    )}
                                </div>

                                {/* Product Type Dropdown */}
                                <div className="md:col-span-2"> {/* Full width */}
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Product Type *
                                    </label>
                                    <select
                                        name="product_type"
                                        value={formData.product_type}
                                        onChange={handleInputChange}
                                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors.product_type ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        disabled={submitting}
                                    >
                                        <option value="">Select Product Type</option>
                                        {producttypelist && producttypelist.map((item, index) =>
                                            <option value={item.id}>{item.name}</option>
                                        )}
                                    </select>
                                    {formErrors.product_type && (
                                        <p className="text-red-500 text-xs mt-1">{formErrors.product_type}</p>
                                    )}
                                </div>

                                {/* Total Quantity */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Total Quantity *
                                    </label>
                                    <input
                                        type="number"
                                        name="total_qty"
                                        value={formData.total_qty}
                                        onChange={handleInputChange}
                                        min="0"
                                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors.total_qty ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter total quantity"
                                        disabled={submitting}
                                    />
                                    {formErrors.total_qty && (
                                        <p className="text-red-500 text-xs mt-1">{formErrors.total_qty}</p>
                                    )}
                                </div>

                                {/* Buying Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Buying GT coins *
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500"></span>
                                        <input
                                            type="number"
                                            name="buying_price"
                                            value={formData.buying_price}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="0.01"
                                            className={`w-full p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors.buying_price ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="0.00"
                                            disabled={submitting}
                                        />
                                    </div>
                                    {formErrors.buying_price && (
                                        <p className="text-red-500 text-xs mt-1">{formErrors.buying_price}</p>
                                    )}
                                </div>

                                {/* Selling Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Selling GT coins *
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500"></span>
                                        <input
                                            type="number"
                                            name="selling_price"
                                            value={formData.selling_price}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="0.01"
                                            className={`w-full p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-[#f4a406] focus:border-transparent ${formErrors.selling_price ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="0.00"
                                            disabled={submitting}
                                        />
                                    </div>
                                    {formErrors.selling_price && (
                                        <p className="text-red-500 text-xs mt-1">{formErrors.selling_price}</p>
                                    )}
                                </div>

                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 py-2 px-4 bg-[#f4a406] text-white rounded-lg hover:bg-[#d89005] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Saving...' : modalType === 'add' ? 'Add Product' : 'Update Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}